import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useLocation, useNavigate } from '@tanstack/react-router'
import {
  SortingState,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { type JSX, useCallback, useMemo, useState } from 'react'
import EmptyState from '~/components/empty-state'
import { useDatastore } from '~/features/datastore'
import { Table } from '~/features/table/ui/table'
import { useTRPC } from '~/services/trpc'
import {
  DEFAULT_WORKSPACE_FILTER_VALUE,
  SettingsActions,
  SettingsContent,
  useSettingTitle,
} from '~/templates/settings'

import { FilterCollection } from '@hap/analytics'
import {
  Button,
  Icon,
  Search,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@hap/components'
import { get } from '@hap/utils'

import { INTENT_FALLBACK_DATA, columns } from './-components/table-config'

export const Route = createFileRoute('/_authenticated/$alias/settings/intents/')({
  component: IntentsSettings,
})

export default function IntentsSettings(): JSX.Element {
  const trpc = useTRPC()
  const location = useLocation()
  const navigate = useNavigate({ from: '/$alias/settings' })
  const search = location.search as Record<string, unknown>
  const query = (search.query as string) || ''
  const filters = (search.filters as Record<string, unknown>) || {}

  const setQuery = useCallback(
    (value: string | undefined) => {
      navigate({
        to: location.pathname,
        search: {
          ...search,
          query: value,
          page: 1,
        },
        replace: true,
      })
    },
    [location.pathname, navigate, search],
  )

  const updateFilters = useCallback(
    (filterUpdates: Record<string, unknown>) => {
      navigate({
        to: location.pathname,
        search: {
          ...search,
          filters: { ...filters, ...filterUpdates },
          page: 1,
        },
        replace: true,
      })
    },
    [filters, location.pathname, navigate, search],
  )
  const { data: workspaces } = useDatastore(FilterCollection.Workspaces)

  useSettingTitle([
    {
      label: 'Intents',
      description:
        'Intents represent the goals or purposes behind customer interactions.',
    },
  ])

  const workspaceFilter: string = get(
    filters,
    'workspaceId',
    DEFAULT_WORKSPACE_FILTER_VALUE,
  )

  const [newIntent, setNewIntent] = useState<
    | false
    | {
        id: string
        label: string
        workspaceId: string | null
        isActive: boolean
        suggested: string | null
        createdAt: string
        updatedAt: string
        editing: boolean
        onSubmit: () => void
      }
  >(false)

  const { data, isLoading } = useQuery(trpc.intents.list.queryOptions())

  const [sort, setSort] = useState<SortingState>([{ id: 'label', desc: false }])

  const intents = useMemo(() => {
    if (!data) return INTENT_FALLBACK_DATA
    let filtered = data

    // Apply search filter
    if (query) {
      filtered = filtered.filter((intent) =>
        intent.label.toLowerCase().includes(query.toLowerCase()),
      )
    }

    // Apply workspace filter
    if (workspaceFilter === 'Organization') {
      filtered = filtered.filter((intent) => !intent.workspaceId)
    } else if (workspaceFilter !== 'All') {
      filtered = filtered.filter((intent) => intent.workspaceId === workspaceFilter)
    }

    // Apply status filter
    // if (statusFilter === 'confirmed') {
    //   filtered = filtered.filter((intent) => intent.isActive)
    // } else if (statusFilter === 'suggested') {
    //   filtered = filtered.filter((intent) => !intent.isActive)
    // }

    return filtered
  }, [data, query, workspaceFilter])

  const onSubmit = useCallback(() => {
    setNewIntent(false)
  }, [])

  const handleAddIntent = useCallback(() => {
    setNewIntent({
      id: '',
      label: '',
      workspaceId: null,
      isActive: true,
      suggested: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      editing: true,
      onSubmit,
    })
  }, [onSubmit])

  const tableData = useMemo(() => {
    return newIntent ? [newIntent, ...intents] : intents
  }, [newIntent, intents])

  const table = useReactTable({
    columns: columns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: false,
    onSortingChange: setSort,
    state: {
      sorting: sort,
    },
    defaultColumn: {
      size: 0,
      minSize: 0,
      maxSize: Number.MAX_SAFE_INTEGER,
    },
  })

  return (
    <>
      <SettingsActions>
        <Button onClick={handleAddIntent}>Add intent</Button>
      </SettingsActions>

      <SettingsContent spacing="sm">
        <header className="flex justify-between gap-3">
          <div className="flex items-center gap-2">
            <Search
              placeholder="Search by name"
              onSearch={(value) => setQuery(value || undefined)}
              className="flex w-125 max-w-md shrink-0 grow"
              defaultValue={query}
            />

            <Select
              value={workspaceFilter}
              onValueChange={(value) => {
                updateFilters({ workspaceId: value })
              }}
            >
              <SelectTrigger
                size="sm-wd"
                className="w-50"
                variant="ghost"
                data-isdirty={DEFAULT_WORKSPACE_FILTER_VALUE !== workspaceFilter}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All workspaces</SelectItem>
                <SelectItem value="Organization">Organization-wide</SelectItem>
                {workspaces?.map((workspace) => (
                  <SelectItem key={workspace.id} value={workspace.id}>
                    {workspace.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </header>
        {data?.length === 0 && !newIntent ? (
          <EmptyState
            icon={<Icon name="crosshair-simple" size={32} weight="fill" />}
            title="Intents"
            description="Intents represent the goals or purposes behind customer interactions. They help understand what customers are trying to accomplish."
            actions={[{ label: 'Add intent', onClick: handleAddIntent }]}
          />
        ) : (
          <Table table={table} isLoading={isLoading} />
        )}
      </SettingsContent>
    </>
  )
}
